import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Box,
  Alert,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  SelectChangeEvent,
} from "@mui/material";
import {
  IDiseaseItem,
  DataType,
  handleUpdateDiseaseBurden,
  IHealthAreaItem,
  IFilters,
} from "../../../api/mosaic/health_area";
import ModalWrapper, { modalCancelButtonSx, modalSubmitButtonSx } from "../../shared/ModalWrapper";
import theme from "../../../utils/theme";

interface EditDiseaseModalProps {
  healthAreas: IHealthAreaItem[];
  diseaseToEdit?: IDiseaseItem;
  isOpen: boolean;
  handleClose: () => void;
  onUpdate: () => void;
}

interface IDiseaseFormData {
  name: string;
  prevalenceReference: string;
  icode: string;
  source: string;
  sourceYear: number;
  disclaimer: string;
  dataType: DataType;
  healthAreaCategoryId: number;
  filters: IFilters;
}

const InitialDiseaseFormValue: IDiseaseFormData = {
  name: "",
  prevalenceReference: "",
  icode: "",
  source: "",
  sourceYear: new Date().getFullYear(),
  disclaimer: "",
  dataType: DataType.Prevalence,
  healthAreaCategoryId: -1,
  filters: { measure_type: false, race: true, sex: true, age: true },
};

const EditDiseaseModal: React.FC<EditDiseaseModalProps> = ({
  healthAreas,
  diseaseToEdit,
  isOpen,
  handleClose,
  onUpdate,
}) => {
  const [formData, setFormData] = useState<IDiseaseFormData>(InitialDiseaseFormValue);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  useEffect(() => {
    if (diseaseToEdit) {
      setFormData({
        name: diseaseToEdit.name || "",
        prevalenceReference: diseaseToEdit.prevalenceReference || "",
        icode: diseaseToEdit.icode || "",
        source: diseaseToEdit.source || "",
        sourceYear: diseaseToEdit.sourceYear || new Date().getFullYear(),
        disclaimer: diseaseToEdit.disclaimer || "",
        dataType: diseaseToEdit.dataType || DataType.Prevalence,
        healthAreaCategoryId: diseaseToEdit.healthAreaCategoryId || (healthAreas.length > 0 ? healthAreas[0].id : -1),
        filters: diseaseToEdit.filters || { measure_type: false, race: true, sex: true, age: true },
      });
      setErrors({});
      setSubmissionError(null);
    }
  }, [diseaseToEdit, healthAreas]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = "Name is required.";
    if (!formData.prevalenceReference.trim()) newErrors.prevalenceReference = "Prevalence Reference is required.";
    if (!formData.icode.trim()) newErrors.icode = "ICode is required.";
    if (!formData.source.trim()) newErrors.source = "Source is required.";
    if (!/^\d{4}$/.test(formData.sourceYear.toString())) newErrors.sourceYear = "Valid 4-digit year required.";
    if (!formData.disclaimer.trim()) newErrors.disclaimer = "Disclaimer is required.";
    if (!formData.dataType) newErrors.dataType = "Data Type is required.";
    if (!formData.healthAreaCategoryId || formData.healthAreaCategoryId === -1) newErrors.healthAreaCategoryId = "Health Area is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: name === "sourceYear" ? Number(value) : value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleDataTypeChange = (event: SelectChangeEvent<DataType>) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      dataType: value as DataType,
      filters: value === DataType.Prevalence ? { ...prev.filters, measure_type: false } : prev.filters,
    }));
  };

  const handleHealthAreaChange = (event: SelectChangeEvent<string>) => {
    setFormData((prev) => ({ ...prev, healthAreaCategoryId: Number(event.target.value) }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, filters: { ...prev.filters, [name]: checked } }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    setSubmissionError(null);
    try {
      await handleUpdateDiseaseBurden(
        diseaseToEdit!.id, formData.name, formData.prevalenceReference, formData.icode,
        formData.source, formData.sourceYear, formData.disclaimer, formData.dataType,
        formData.healthAreaCategoryId, formData.filters,
      );
      setIsSubmitting(false);
      onUpdate();
      handleClose();
    } catch (error: unknown) {
      setSubmissionError("Failed to update disease.");
      setIsSubmitting(false);
    }
  };

  return (
    <ModalWrapper
      open={isOpen}
      onClose={handleClose}
      title="Edit Disease"
      actions={
        <>
          <Button variant="outlined" onClick={handleClose} disabled={isSubmitting} sx={modalCancelButtonSx}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            sx={modalSubmitButtonSx}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </>
      }
    >
      {submissionError && (
        <Box mb={2}>
          <Alert severity="error">{submissionError}</Alert>
        </Box>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <FormControl fullWidth required error={Boolean(errors.healthAreaCategoryId)}>
          <InputLabel>Health Area</InputLabel>
          <Select
            label="Health Area"
            value={formData.healthAreaCategoryId.toString()}
            onChange={handleHealthAreaChange}
          >
            {healthAreas.map((ha) => (
              <MenuItem key={ha.id} value={ha.id}>{ha.name}</MenuItem>
            ))}
          </Select>
          {errors.healthAreaCategoryId && <Typography variant="caption" color="error">{errors.healthAreaCategoryId}</Typography>}
        </FormControl>

        <FormControl fullWidth required error={Boolean(errors.dataType)}>
          <InputLabel>Data Type</InputLabel>
          <Select label="Data Type" value={formData.dataType} onChange={handleDataTypeChange}>
            <MenuItem value={DataType.Incidence}>Incidence</MenuItem>
            <MenuItem value={DataType.Prevalence}>Prevalence</MenuItem>
          </Select>
        </FormControl>

        <TextField fullWidth label="Disease Name" name="name" value={formData.name} onChange={handleInputChange} required error={!!errors.name} helperText={errors.name} />
        <TextField fullWidth label="Prevalence Reference" name="prevalenceReference" value={formData.prevalenceReference} onChange={handleInputChange} required error={!!errors.prevalenceReference} helperText={errors.prevalenceReference} />
        <TextField fullWidth label="ICode" name="icode" value={formData.icode} onChange={handleInputChange} required error={!!errors.icode} helperText={errors.icode} />
        <TextField fullWidth label="Source" name="source" value={formData.source} onChange={handleInputChange} required error={!!errors.source} helperText={errors.source} />
        <TextField fullWidth label="Source Year" name="sourceYear" type="number" value={formData.sourceYear} onChange={handleInputChange} required error={!!errors.sourceYear} helperText={errors.sourceYear} />
        <TextField fullWidth label="Disclaimer" name="disclaimer" value={formData.disclaimer} onChange={handleInputChange} required multiline rows={3} error={!!errors.disclaimer} helperText={errors.disclaimer} />

        <Box>
          <Typography variant="body2" sx={{ color: "#878787", mb: 1 }}>Filters</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            <FormControlLabel label="Measure Type" control={<Checkbox name="measure_type" checked={formData.filters.measure_type} onChange={handleCheckboxChange} disabled={formData.dataType !== DataType.Incidence} sx={{ "&.Mui-checked": { color: theme.palette.brand.primary } }} />} />
            <FormControlLabel label="Race/Ethnicity" control={<Checkbox name="race" checked={formData.filters.race} onChange={handleCheckboxChange} sx={{ "&.Mui-checked": { color: theme.palette.brand.primary } }} />} />
            <FormControlLabel label="Age" control={<Checkbox name="age" checked={formData.filters.age} onChange={handleCheckboxChange} sx={{ "&.Mui-checked": { color: theme.palette.brand.primary } }} />} />
            <FormControlLabel label="Sex" control={<Checkbox name="sex" checked={formData.filters.sex} onChange={handleCheckboxChange} sx={{ "&.Mui-checked": { color: theme.palette.brand.primary } }} />} />
          </Box>
        </Box>
      </Box>
    </ModalWrapper>
  );
};

export default EditDiseaseModal;
